import { useMemo } from 'react'

import { useCatalogListQueries } from 'features/catalog/hooks/use-catalog-list-queries'
import type { FocusListOption } from 'features/catalog/types'
import { assembleCatalogTableRows } from 'features/catalog/utils/build-catalog-rows'
import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'
// bully ignore[no-cross-feature-private-imports: the established v1<->v2 translator seam already used by use-charts-v2-state.ts, dashboards-v2, and catalog's own detail Container. follow-up: .agents/follow-ups/bully-typescript-cross-feature-hooks.md]
import { translatePanelToV1Filters } from 'features/charts-v2/utils/translate-v2-to-v1-filters'
// bully ignore[no-cross-feature-private-imports: the established panel-scoped filter-options seam every v2-native page uses (Feedback/Keywords/Charts). follow-up: .agents/follow-ups/bully-typescript-cross-feature-hooks.md]
import { useEffectiveFilterList } from 'features/filters/hooks/use-effective-filter-list'
// bully ignore[no-cross-feature-private-imports: same panel-scoped filter-options seam as useEffectiveFilterList above — called a second time (react-query dedupes by query key, no extra request) purely to read its isLoading, which useEffectiveFilterList doesn't expose. follow-up: .agents/follow-ups/bully-typescript-cross-feature-hooks.md]
import { usePanelFilterOptions } from 'features/filters/hooks/use-panel-filter-options'
import type { FilterOption, FilterOptionSelect } from 'features/filters/types'

export type CatalogTableRowsResult = {
  rows: FocusListOption[]
  isLoading: boolean
}

/**
 * Assembles the table's row set for the current lens. Row identity + counts
 * come from the panel-scoped `/post/filter/options/v2` response (via
 * `useEffectiveFilterList`) for every lens, so editing any sidebar filter —
 * not just the date range — changes which rows appear. Enrichment (thumbnail,
 * item count, country breakdown) stays on the date-only `useCatalogListQueries`
 * and is left-joined by value — see `build-catalog-rows.ts`'s file header.
 *
 * Requests `faceted: false`: the sidebar's fetch strips a field's own filter
 * before counting it (picker shows non-zero counts for unselected values);
 * this table IS the row list, so the filter must narrow its own rows.
 */
export function useCatalogTableRows(params: {
  projectId: string
  selectedLens: string
  panel: ChartPanel
  defaultFilterList: FilterOption[]
  groupCountries: boolean
  groupThemes: boolean
}): CatalogTableRowsResult {
  const {
    projectId,
    selectedLens,
    panel,
    defaultFilterList,
    groupCountries,
    groupThemes,
  } = params

  const localFilters = useMemo(() => translatePanelToV1Filters(panel), [panel])

  const {
    hierarchyData,
    hierarchyLoading,
    sourceData,
    sourcesLoading,
    syndicatedSourceData,
    syndicatedSourceLoading,
  } = useCatalogListQueries(projectId, selectedLens, localFilters)

  const filterOptions = useEffectiveFilterList({
    projectId,
    panel,
    defaultFilterList,
    fields: [selectedLens],
    faceted: false,
  })

  // Dedupes against the query above; `faceted` must match or the two land
  // on different keys — this just reads the shared loading state.
  const { isLoading: filterOptionsLoading } = usePanelFilterOptions(
    projectId,
    panel,
    { fields: [selectedLens], faceted: false },
  )

  const filterValues = useMemo(() => {
    const opt = filterOptions.find((el) => el.field === selectedLens) as
      | FilterOptionSelect
      | undefined
    return opt?.values ?? []
  }, [filterOptions, selectedLens])

  // Active whenever anything beyond `panel.dateRange` constrains the query.
  const hasActiveFilter =
    panel.filters.length > 0 ||
    panel.searchTerms.length > 0 ||
    Object.keys(panel.searchCriteria).length > 0

  const rows = useMemo(
    () =>
      assembleCatalogTableRows({
        selectedLens,
        filterValues,
        hierarchyData,
        sourceData,
        syndicatedSourceData,
        groupCountries,
        groupThemes,
        hasActiveFilter,
      }),
    [
      selectedLens,
      filterValues,
      hierarchyData,
      sourceData,
      syndicatedSourceData,
      groupCountries,
      groupThemes,
      hasActiveFilter,
    ],
  )

  const isLoading =
    filterOptionsLoading ||
    (selectedLens === 'product_hierarchy' && hierarchyLoading) ||
    (selectedLens === 'source_type' &&
      (sourcesLoading || syndicatedSourceLoading))

  return { rows, isLoading }
}
